import { NavigateFunction } from 'react-router-dom';
import API_CONSTANT from '../../constants/APIConstant';
import { get, putJson, ApiResult } from '../apiClient';

// Types mirror the backend's Java records in
// com.ihome.app.dto.subscription.* verbatim (field order + names). Responses
// are the DTO/list directly — the backend never wraps in `{data: ...}`
// (apiClient's ApiResult<T>.data is a client-side convention, not something
// the backend sends). LocalDate/LocalDateTime serialize as plain ISO strings
// ("YYYY-MM-DD" / "YYYY-MM-DDTHH:mm:ss") — no custom Jackson date config in
// the backend, so Spring Boot's default (non-timestamp) JavaTimeModule
// behavior applies. Money (BigDecimal) comes across as a JSON number.

export interface FeatureDTO {
    code: string;
    name: string;
    description: string | null;
}

export interface PlanDTO {
    id: number;
    code: string;
    name: string;
    pricePerFlat: number;
    features: FeatureDTO[];
    description: string | null;
    // Boxed BigDecimal on the backend — null when there's no flat-count context
    // to price it against (e.g. no society context available).
    estimatedTotal: number | null;
}

export interface AgentContactDTO {
    name: string | null;
    mobile: string | null;
}

export interface SubscriptionDTO {
    societyId: number;
    status: string;
    effectiveStatus: string;
    featuresUnlocked: boolean;
    plan: PlanDTO | null;
    trialMonths: number | null;
    trialStartDate: string | null;
    trialEndDate: string | null;
    subscriptionStartDate: string | null;
    subscriptionEndDate: string | null;
    billingCycle: string | null;
    // Monthly rate per flat — see subscriptionMeta's cycleMultiplier for turning this (via
    // totalPrice) into a per-cycle billed amount.
    pricePerFlat: number | null;
    flatCount: number | null;
    // Monthly total: pricePerFlat × flatCount. Not adjusted for billing cycle.
    totalPrice: number | null;
    // totalPrice scaled by billingCycle (×1 MONTHLY, ×3 QUARTERLY, ×12 YEARLY) — what actually
    // gets billed each cycle.
    cycleTotal: number | null;
    daysLeft: number | null;
    notes: string | null;
    // Defensively nullable on the client: a backend deployed before this field existed (or a
    // stale cached response) would omit it entirely, not send an empty array.
    agents?: AgentContactDTO[] | null;
}

export interface SubscriptionStatusDTO {
    planName: string | null;
    effectiveStatus: string;
    featuresUnlocked: boolean;
    daysLeft: number | null;
    endDate: string | null;
    // Plan feature codes. Absent on older backends — treat as unknown (nothing locked).
    features?: string[] | null;
}

// All fields nullable on the wire — null means "leave unchanged" for the
// agent PUT. planId is required conceptually but still nullable in the type
// since the backend accepts (and ignores) a null there too.
export interface AgentSubscriptionUpdateRequest {
    planId: number | null;
    status: string | null;
    trialMonths: number | null;
    trialStartDate: string | null;
    subscriptionStartDate: string | null;
    subscriptionEndDate: string | null;
    billingCycle: string | null;
    pricePerFlat: number | null;
    flatCount: number | null;
    notes: string | null;
    // When true, clears subscriptionEndDate back to open-ended. Mutually exclusive with a
    // non-null subscriptionEndDate on the same request — the backend 400s if both are sent.
    clearSubscriptionEndDate?: boolean | null;
}

// Every function returns the raw ApiResult<T> (not throw-on-failure) so
// callers can branch on `res.status` (e.g. 404 "no subscription yet" vs a
// generic failure) the same way FundAPI.getAdminStatementAPI does — the one
// existing precedent in this codebase for a status-sensitive GET.

/** GET /v1/subscription — the caller's own society's subscription. SOCIETY_ADMIN, AGENT. */
export const getSubscription = (navigate: NavigateFunction): Promise<ApiResult<SubscriptionDTO>> =>
    get<SubscriptionDTO>(navigate, API_CONSTANT.PATH.SUBSCRIPTION.GET);

/** GET /v1/subscription/plans — active plans priced against the caller's society. SOCIETY_ADMIN, AGENT. */
export const getPlans = (navigate: NavigateFunction): Promise<ApiResult<PlanDTO[]>> =>
    get<PlanDTO[]>(navigate, API_CONSTANT.PATH.SUBSCRIPTION.PLANS);

/** GET /v1/subscription/status — every society role. 404 when the society has no subscription row. */
export const getSubscriptionStatus = (navigate: NavigateFunction): Promise<ApiResult<SubscriptionStatusDTO>> =>
    get<SubscriptionStatusDTO>(navigate, API_CONSTANT.PATH.SUBSCRIPTION.STATUS);

/** GET /v1/agent/societies/{societyId}/subscription — AGENT only, notes included. */
export const getAgentSubscription = (
    navigate: NavigateFunction,
    societyId: number,
): Promise<ApiResult<SubscriptionDTO>> =>
    get<SubscriptionDTO>(navigate, API_CONSTANT.PATH.AGENT.SOCIETY_SUBSCRIPTION(societyId));

/** PUT /v1/agent/societies/{societyId}/subscription — AGENT only. Null fields = unchanged. */
export const updateAgentSubscription = (
    navigate: NavigateFunction,
    societyId: number,
    body: Partial<AgentSubscriptionUpdateRequest>,
): Promise<ApiResult<SubscriptionDTO>> =>
    putJson<SubscriptionDTO>(navigate, API_CONSTANT.PATH.AGENT.SOCIETY_SUBSCRIPTION(societyId), body);
